import type { SampleToolResultSectionGradingsPayload } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { Size } from "@repo/ui/helpers/helpers";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import type { FC } from "react";
import type { GradedCallsTab } from "./gradedCallsSlideover.js";
import { SectionFeedbackGradingsTable } from "./sectionFeedbackGradingsTable.js";
import { SectionSampleGradingsTable } from "./sectionSampleGradingsTable.js";

const GRADED_CALLS_TABS = [
  { id: "sample", label: "Sample calls" },
  { id: "feedback", label: "Feedback calls" },
] as const;

interface SectionGradedCallsSlideoverProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTab: GradedCallsTab;
  onSelectTab: (tab: GradedCallsTab) => void;
  gradings: SampleToolResultSectionGradingsPayload | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const SectionGradedCallsSlideover: FC<
  SectionGradedCallsSlideoverProps
> = ({
  isOpen,
  onClose,
  selectedTab,
  onSelectTab,
  gradings,
  isLoading,
  isError,
}) => {
  return (
    <SlideoverContainer
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen) {
          onClose();
        }
      }}
      isDismissable
      size={Size.LARGE}
    >
      <SlideoverDialog label="Graded calls">
        <SlideoverDialogTitleHeader>Graded calls</SlideoverDialogTitleHeader>
        <SlideoverDialogContent>
          <div className="px-4 py-2">
            <SmallTabs
              tabs={GRADED_CALLS_TABS.map((tab) => tab.label)}
              selectedTabIndex={GRADED_CALLS_TABS.findIndex(
                (tab) => tab.id === selectedTab,
              )}
              onSelectTabIndex={(index) => {
                const tab = GRADED_CALLS_TABS[index];
                if (tab) {
                  onSelectTab(tab.id);
                }
              }}
            />
          </div>
          {selectedTab === "sample" ? (
            <>
              <p className="px-4 py-2 text-sm text-muted-foreground">
                Calls automatically selected and regraded to test this section
                update against the current baseline.
              </p>
              <SectionSampleGradingsTable
                entries={gradings?.sample_grades ?? []}
                isLoading={isLoading}
                isError={isError}
              />
            </>
          ) : (
            <>
              <p className="px-4 py-2 text-sm text-muted-foreground">
                Calls regraded against grading feedback left by your teammates.
              </p>
              <SectionFeedbackGradingsTable
                entries={gradings?.feedback_grades ?? []}
                isLoading={isLoading}
                isError={isError}
              />
            </>
          )}
        </SlideoverDialogContent>
      </SlideoverDialog>
    </SlideoverContainer>
  );
};
